import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { QuoteApprovalModal } from '../components/domain/QuoteApprovalModal';
import { PaymentModal } from '../components/domain/PaymentModal';
import { formatDate, formatCurrency } from '../utils/calculations';
import { CheckCircleIcon, FileTextIcon, CreditCardIcon } from 'lucide-react';
export function PublicInvoiceView() {
  const {
    id
  } = useParams();
  const {
    getInvoiceById,
    getLineItemsByInvoiceId,
    getCustomerById,
    selectedCompany
  } = useData();
  const [showQuoteApproval, setShowQuoteApproval] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const invoice = id ? getInvoiceById(id) : null;
  const lineItems = id ? getLineItemsByInvoiceId(id) : [];
  const customer = invoice ? getCustomerById(invoice.customer_id) : null;
  // Show not found message without redirecting
  if (!invoice || !customer || !selectedCompany) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-476">
        <Card className="max-w-md w-full text-center py-12" data-id="element-477">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-478">
            <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-479" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-480">
            Invoice Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-481">
            The invoice you're looking for doesn't exist or has been removed.
          </p>
          {id && <p className="text-sm text-gray-500 font-mono" data-id="element-482">Invoice ID: {id}</p>}
        </Card>
      </div>;
  }
  const isQuote = invoice.status === 'quote';
  const isPaid = invoice.status === 'paid' || invoice.status === 'approved';
  const canPay = invoice.status === 'sent';
  const getStatusColor = () => {
    switch (invoice.status) {
      case 'paid':
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'quote':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'sent':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-483">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm" data-id="element-484">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-485">
          <div className="flex items-center gap-4" data-id="element-486">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-487">
              <FileTextIcon className="w-6 h-6 text-white" data-id="element-488" />
            </div>
            <div data-id="element-489">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-490">
                {selectedCompany.name}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-491">
                {isQuote ? 'Quote' : 'Invoice'} #{invoice.id.slice(0, 8)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-492">
        <div className="space-y-6" data-id="element-493">
          {/* Status Banner */}
          <div className={`rounded-xl border-2 p-6 ${getStatusColor()}`} data-id="element-494">
            <div className="flex items-center justify-between" data-id="element-495">
              <div data-id="element-496">
                <h2 className="text-lg font-semibold mb-1" data-id="element-497">
                  {isPaid && 'Payment Received'}
                  {isQuote && 'Quote Ready for Approval'}
                  {canPay && 'Payment Required'}
                  {invoice.status === 'draft' && 'Draft'}
                  {invoice.status === 'refunded' && 'Refunded'}
                  {invoice.status === 'voided' && 'Voided'}
                </h2>
                <p className="text-sm opacity-90" data-id="element-498">
                  {isPaid && 'Thank you for your payment!'}
                  {isQuote && 'Please review and approve this quote to proceed'}
                  {canPay && 'Please complete payment to process your order'}
                </p>
              </div>
              {isPaid && <CheckCircleIcon className="w-12 h-12 opacity-50" data-id="element-499" />}
            </div>
          </div>

          {/* Invoice Details */}
          <Card data-id="element-500">
            <div className="space-y-6" data-id="element-501">
              {/* Header Info */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-6 border-b border-gray-200" data-id="element-502">
                <div data-id="element-503">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-504">
                    Bill To
                  </h3>
                  <p className="font-semibold text-gray-900" data-id="element-505">{customer.name}</p>
                  <p className="text-sm text-gray-600" data-id="element-506">{customer.email}</p>
                  <p className="text-sm text-gray-600" data-id="element-507">{customer.address}</p>
                  <p className="text-sm text-gray-600" data-id="element-508">{customer.phone}</p>
                </div>
                <div className="text-left sm:text-right" data-id="element-509">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-510">
                    {isQuote ? 'Quote' : 'Invoice'} Details
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-511">
                    Date: {formatDate(invoice.date)}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-512">
                    ID: {invoice.id.slice(0, 12)}
                  </p>
                </div>
              </div>

              {/* Line Items */}
              <div data-id="element-513">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-514">
                  Items
                </h3>
                <div className="space-y-3" data-id="element-515">
                  {lineItems.map(item => <div key={item.id} className="flex justify-between items-start gap-4 py-3 border-b border-gray-100 last:border-0" data-id="element-516">
                      <div className="flex-1" data-id="element-517">
                        <p className="font-medium text-gray-900" data-id="element-518">
                          {item.description}
                        </p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-519">
                          Quantity: {item.quantity} ×{' '}
                          {formatCurrency(item.price)}
                          {item.is_taxable && <span className="ml-2 text-xs text-gray-400" data-id="element-520">
                              (Taxable)
                            </span>}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-521">
                        <p className="font-semibold text-gray-900" data-id="element-522">
                          {formatCurrency(item.quantity * item.price)}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Totals */}
              <div className="pt-4 border-t border-gray-200" data-id="element-523">
                <TotalsPanel subtotal={invoice.subtotal} taxAmount={invoice.tax_amount} taxRate={invoice.tax_rate} shippingCharge={invoice.shipping_charge} discount={invoice.discount} total={invoice.total_amount} data-id="element-524" />
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          {isQuote && <Card data-id="element-525">
              <div className="text-center py-6" data-id="element-526">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-527">
                  Ready to Proceed?
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-528">
                  Review the quote above and approve to convert it to an invoice
                </p>
                <Button size="lg" onClick={() => setShowQuoteApproval(true)} leftIcon={<CheckCircleIcon className="w-5 h-5" data-id="element-530" />} className="w-full sm:w-auto" data-id="element-529">
                  Approve Quote
                </Button>
              </div>
            </Card>}

          {canPay && <Card data-id="element-531">
              <div className="text-center py-6" data-id="element-532">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-533">
                  Complete Payment
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-534">
                  Total amount due: {formatCurrency(invoice.total_amount)}
                </p>
                <Button size="lg" onClick={() => setShowPayment(true)} leftIcon={<CreditCardIcon className="w-5 h-5" data-id="element-536" />} className="w-full sm:w-auto" data-id="element-535">
                  Pay Now
                </Button>
              </div>
            </Card>}

          {isPaid && <Card data-id="element-537">
              <div className="text-center py-8" data-id="element-538">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-539">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" data-id="element-540" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-541">
                  {invoice.status === 'approved' ? 'Quote Approved' : 'Payment Complete'}
                </h3>
                <p className="text-gray-600" data-id="element-542">Thank you for your business!</p>
              </div>
            </Card>}

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-8 border-t border-gray-200" data-id="element-543">
            <p data-id="element-544">Questions? Contact {selectedCompany.name} for assistance.</p>
          </div>
        </div>
      </main>

      {/* Modals */}
      {isQuote && <QuoteApprovalModal invoice={invoice} lineItems={lineItems} isOpen={showQuoteApproval} onClose={() => setShowQuoteApproval(false)} onApproved={() => {
      setShowQuoteApproval(false);
      window.location.reload();
    }} data-id="element-545" />}

      {canPay && <PaymentModal invoice={invoice} isOpen={showPayment} onClose={() => setShowPayment(false)} data-id="element-546" />}
    </div>;
}